import React, { useState } from "react";
import { SelectField, Form, Grid, SubmitButton, classnames } from "@culturehq/components";
import { Redirect } from "react-router-dom";

import { t, Trans } from "app/utils";
import { useProfile } from "app/context";

import SlackChannelField from "components/event/edit/SlackChannelField";
import EventPortalButton from "../common/EventPortalButton";

import EventForm from "./EventForm";
import styles from "./EventForm.module.scss";

const getInitialInterestNames = eventState => {
  const { interests } = eventState;

  if (interests && interests.length > 0 && interests[0].id) {
    return interests.map(({ name }) => name);
  }
  return eventState.interestNames || [];
};

const getInitialLocationIds = (eventState, profile) => {
  const { locations } = eventState;

  if (locations && locations.length > 0 && locations[0].id) {
    return locations.map(({ id }) => id);
  }
  return eventState.locationIds || [profile.location?.id];
};

const getInitialValues = (eventState, profile) => ({
  ...eventState,
  locationIds: getInitialLocationIds(eventState, profile),
  interestNames: getInitialInterestNames(eventState)
});

const allLocationsOptions = [
  { value: false, label: t("I would like to choose the locations that can see this") },
  { value: true, label: t("All locations can see this") }
];

const Visibility = ({
  eventState, handleEventState, locationOptions, interestOptions, onSubmit, handleUnSaved
}) => {
  const { profile } = useProfile();

  const [allLocations, setAllLocations] = useState(eventState.allLocations);
  const [selectedChannel, setSlackChannel] = useState(eventState.slackChannel);
  const onAllLocationsChange = value => {
    handleUnSaved(true);
    setAllLocations(value);
  };

  if (!eventState.eventType) {
    return <Redirect to="/events/new/type" />;
  }

  const handleSubmit = async values => {
    await handleUnSaved(false);

    if (eventState.id) {
      onSubmit({ ...values });
    } else {
      handleEventState({ ...values, slackChannel: selectedChannel });
    }
  };

  const onSelectedChannel = channel => {
    setSlackChannel(channel);
  };

  return (
    <EventForm icon="megaphone" title={t("Who should see this event?")}>
      <p className={classnames(styles["m-b-35"], styles["f-s-18"])}>
        <Trans>
          Use these options to alert certain groups about your event. Once the
          event is published, you will be able to invite specific groups and
          users.
        </Trans>
      </p>
      <Form
        onSubmit={handleSubmit}
        initialValues={getInitialValues(eventState, profile)}
        id="event_visiblity_form"
        className="event-form"
      >
        <SelectField
          ariaLabel={t("Select interests to promote this event")}
          autoFocus
          className={styles["m-b-20"]}
          name="interestNames"
          options={interestOptions}
          placeholder={t("Search or add new interests to promote this event to the relevant people")}
          creatable
          creatableLabel={t("Create an interest")}
          multiple
          onChange={() => handleUnSaved(true)}
        >
          <Trans>Tag interests to promote this event</Trans>
        </SelectField>
        <Grid className={styles["m-b-20"]}>
          <Grid.Item sm={allLocations ? 12 : 6}>
            <SelectField
              ariaLabel={t("Select if you want specific locations that can see this on the events page")}
              className={styles.allLocations}
              name="allLocations"
              options={allLocationsOptions}
              onChange={onAllLocationsChange}
            >
              <Trans>Locations that can see this on the events page</Trans>
            </SelectField>
          </Grid.Item>
          {!allLocations && (
            <Grid.Item sm={6}>
              <SelectField
                ariaLabel={t("Select event locations")}
                name="locationIds"
                multiple
                options={locationOptions}
                onChange={() => handleUnSaved(true)}
              >
                <Trans>Tag locations that can see this on the Events page </Trans>
              </SelectField>
            </Grid.Item>
          )}
        </Grid>
        {!eventState.id && (
          <SlackChannelField
            publicEvent={eventState.public}
            inviteOnly={eventState.inviteOnly}
            onSelectedChannel={onSelectedChannel}
          />
        )}
        <EventPortalButton>
          {eventState.id
            ? (
              <SubmitButton primary form="event_visiblity_form">
                {submitting => (submitting ? t("Saving...") : t("Save Event"))}
              </SubmitButton>
            )
            : (
              <SubmitButton primary form="event_visiblity_form">
                {submitting => (submitting ? t("Go next...") : t("Next"))}
              </SubmitButton>
            )}
        </EventPortalButton>
      </Form>
    </EventForm>
  );
};

export default Visibility;
